import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface RegionalData {
  state: string;
  total_documents: number;
  total_freight_value?: number;
}

interface BrazilMapChartProps {
  data: RegionalData[];
}

const stateNames: Record<string, string> = {
  'AC': 'Acre', 'AL': 'Alagoas', 'AP': 'Amapá', 'AM': 'Amazonas',
  'BA': 'Bahia', 'CE': 'Ceará', 'DF': 'Distrito Federal', 'ES': 'Espírito Santo',
  'GO': 'Goiás', 'MA': 'Maranhão', 'MT': 'Mato Grosso', 'MS': 'Mato Grosso do Sul',
  'MG': 'Minas Gerais', 'PA': 'Pará', 'PB': 'Paraíba', 'PR': 'Paraná',
  'PE': 'Pernambuco', 'PI': 'Piauí', 'RJ': 'Rio de Janeiro', 'RN': 'Rio Grande do Norte',
  'RS': 'Rio Grande do Sul', 'RO': 'Rondônia', 'RR': 'Roraima', 'SC': 'Santa Catarina',
  'SP': 'São Paulo', 'SE': 'Sergipe', 'TO': 'Tocantins'
};

const BrazilMapChart: React.FC<BrazilMapChartProps> = ({ data }) => {
  const [hoveredState, setHoveredState] = useState<string | null>(null);

  // Encontrar valores máximo e mínimo para escala de cores
  const maxValue = Math.max(...data.map(d => d.total_documents));
  const minValue = Math.min(...data.map(d => d.total_documents));

  // Função para calcular a cor baseada no valor
  const getStateColor = (stateCode: string): string => {
    const stateData = dataByState[stateCode];
    if (!stateData) return 'rgba(229, 231, 235, 0.5)'; // Cinza claro para estados sem dados
    
    const intensity = maxValue === minValue ? 0.8 : 
      0.3 + (0.7 * (stateData.total_documents - minValue) / (maxValue - minValue));
    
    return `rgba(22, 57, 101, ${intensity})`;
  };

  // Função para formatar números
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Criar mapa de dados por estado
  const dataByState = data.reduce((acc, item) => {
    acc[item.state] = item;
    return acc;
  }, {} as Record<string, RegionalData>);

  return (
    <div className="relative w-full mt-8">
      {/* Título do Mapa */}
      <div className="text-center mb-6">
        <h4 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          🗺️ Distribuição Geográfica por Estado
        </h4>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Passe o mouse sobre os estados para ver detalhes
        </p>
      </div>

      {/* Container Principal */}
      <div className="space-y-6">
        {/* Gráfico de Barras Horizontal */}
        <div className="w-full">
          <div
            className="p-6 rounded-xl"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-primary)'
            }}
          >
            <h5 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              📊 Volume por Estado
            </h5>
            <div className="space-y-3">
              {[...data]
                .sort((a, b) => b.total_documents - a.total_documents)
                .map((item, index) => {
                  const percentage = (item.total_documents / maxValue) * 100;
                  const isHovered = hoveredState === item.state;
                  
                  return (
                    <motion.div
                      key={item.state}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="relative"
                      onMouseEnter={() => setHoveredState(item.state)}
                      onMouseLeave={() => setHoveredState(null)}
                    >
                      {/* Label e Valor */}
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-xs font-bold px-2 py-1 rounded min-w-[32px] text-center"
                            style={{
                              backgroundColor: isHovered ? 'var(--primary)' : 'rgba(22, 57, 101, 0.1)',
                              color: isHovered ? 'white' : 'var(--primary)'
                            }}
                          >
                            {item.state}
                          </span>
                          <span
                            className="text-sm font-medium"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {stateNames[item.state]}
                          </span>
                        </div>
                        <span
                          className="text-sm font-bold"
                          style={{ color: 'var(--primary)' }}
                        >
                          {formatNumber(item.total_documents)}
                        </span>
                      </div>

                      {/* Barra de Progresso */}
                      <div
                        className="relative h-8 rounded-lg overflow-hidden"
                        style={{
                          backgroundColor: 'var(--bg-primary)',
                          border: '1px solid var(--border-primary)'
                        }}
                      >
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 1, delay: index * 0.05 }}
                          className="h-full flex items-center justify-end px-3"
                          style={{
                            background: isHovered
                              ? 'linear-gradient(90deg, rgba(22, 57, 101, 0.9) 0%, rgba(22, 57, 101, 1) 100%)'
                              : 'linear-gradient(90deg, rgba(22, 57, 101, 0.6) 0%, rgba(22, 57, 101, 0.8) 100%)',
                            transition: 'background 0.3s ease'
                          }}
                        >
                          {item.total_freight_value && (
                            <span className="text-xs font-bold text-white">
                              {formatCurrency(item.total_freight_value)}
                            </span>
                          )}
                        </motion.div>
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total de Estados */}
          <div
            className="p-4 rounded-xl text-center"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '2px solid var(--border-primary)'
            }}
          >
            <div className="text-3xl font-bold mb-1" style={{ color: 'var(--primary)' }}>
              {data.length}
            </div>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Estados com Dados
            </div>
          </div>

          {/* Total de Documentos */}
          <div
            className="p-4 rounded-xl text-center"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '2px solid var(--border-primary)'
            }}
          >
            <div className="text-3xl font-bold mb-1" style={{ color: 'var(--primary)' }}>
              {formatNumber(data.reduce((sum, item) => sum + item.total_documents, 0))}
            </div>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Total de Documentos
            </div>
          </div>

          {/* Valor Total */}
          {data.some(item => item.total_freight_value) && (
            <div
              className="p-4 rounded-xl text-center"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '2px solid var(--border-primary)'
              }}
            >
              <div className="text-2xl font-bold mb-1" style={{ color: 'var(--primary)' }}>
                {formatCurrency(data.reduce((sum, item) => sum + (item.total_freight_value || 0), 0))}
              </div>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Valor Total de Frete
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrazilMapChart;

